import { useState, ReactNode, useEffect } from "react";
import { EXERCISE_TAGS, EXERCISE_TYPES, MuscleDistribution, FATICA_SISTEMICA, PROFILO_RESISTENZA } from "../../libs/exercises/types";
import { getCategoryLabel, getTypeLabel } from "../../libs/exercises/utils";
import { Exercise } from "../../libs/exercises/types";
import { UseFormSetValue, useForm } from "react-hook-form";
import { PlusButton } from "../Button";
import { MuscleDistributionForm } from "./MuscleDistributionForm";
import { useGetSpecialCategoriesQuery } from "../../libs/specialCategory/api";

type ExerciseFormProps = {
  register: any;
  errors: any;
  onSubmit: () => void;
  formBtn: ReactNode;
  exerciseToUpdate?: Exercise;
  setValue: UseFormSetValue<any>;
};

export const ExerciseForm: React.FC<ExerciseFormProps> = ({
  register,
  errors,
  onSubmit,
  formBtn,
  exerciseToUpdate,
  setValue,
}) => {
  const {
    register: registerTag,
    formState: { errors: errorsTag },
    handleSubmit,
    setValue: setValueTag,
  } = useForm<{ tag: string }>();
  const [tags, setTags] = useState<string[]>(exerciseToUpdate?.tags || []);
  const [muscleDistribution, setMuscleDistribution] = useState<MuscleDistribution[]>(() => {
    const md = exerciseToUpdate?.muscleDistribution || [];
    // Se c'è un solo distretto, forza 100%
    if (md.length === 1) return [{ ...md[0], percentage: 100 }];
    return md;
  });
  const [isSpecial, setIsSpecial] = useState(exerciseToUpdate?.specialCategoryId != null);
  const [specialCategoryId, setSpecialCategoryId] = useState<number | undefined>(
    exerciseToUpdate?.specialCategoryId
  );
  const { data: specialCategories } = useGetSpecialCategoriesQuery();

  const { name } = register("tags");

  useEffect(() => {
    setValue("tags", tags);
  }, [tags]);

  useEffect(() => {
    setValue("muscleDistribution", muscleDistribution);
  }, [muscleDistribution]);

  useEffect(() => {
    setValue("specialCategoryId", isSpecial ? specialCategoryId : undefined);
  }, [specialCategoryId, isSpecial]);

  const onTagSubmit = (data: { tag: string }) => {
    setTags([...tags, data.tag.toUpperCase()]);
    setValueTag("tag", "");
  };

  return (
    <form className="space-y-6 p-4" onSubmit={onSubmit}>
      <div>
        <label
          htmlFor="text"
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          Nome Esercizio
        </label>
        <input
          {...register("name", { required: "Inserisci un valore valido" })}
          aria-invalid={errors.name ? "true" : "false"}
          type="name"
          id="name"
          defaultValue={exerciseToUpdate?.name}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          placeholder="Nome Esercizio"
          required
        />
      </div>
      {errors.name && (
        <p
          role="alert"
          className="block mb-2 text-sm font-medium text-red-700 dark:text-red-500"
        >
          {errors.name?.message?.toString()}
        </p>
      )}
      <div>
        <label
          htmlFor="text"
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          Link Video
        </label>
        <input
          {...register("video", { required: "Inserisci un link valido" })}
          aria-invalid={errors.name ? "true" : "false"}
          type="video"
          id="video"
          defaultValue={exerciseToUpdate?.videoUrls[0]}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          placeholder="Inserisci il link del video di esempio"
          required
        />
      </div>
      {errors.video && (
        <p
          role="alert"
          className="block mb-2 text-sm font-medium text-red-700 dark:text-red-500"
        >
          {errors.video?.message?.toString()}
        </p>
      )}

      {/* Checkbox esercizio speciale */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isSpecial}
            onChange={(e) => {
              setIsSpecial(e.target.checked);
              if (!e.target.checked) {
                setSpecialCategoryId(undefined);
              }
            }}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-500 w-4 h-4 dark:bg-gray-700"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Esercizio speciale (rehab, prehab, mobilità, ecc.)
          </span>
        </label>

        {isSpecial && (
          <div className="mt-3">
            <label className="block mb-1 text-xs text-gray-500 dark:text-gray-400">
              Seleziona la categoria
            </label>
            {!specialCategories?.length ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Nessuna categoria creata. Vai al tab "Categorie Speciali" per aggiungerne.
              </p>
            ) : (
              <select
                value={specialCategoryId ?? ""}
                onChange={(e) =>
                  setSpecialCategoryId(e.target.value ? parseInt(e.target.value) : undefined)
                }
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">-- Seleziona categoria --</option>
                {specialCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Distribuzione muscolare — sempre visibile (opzionale) */}
      <MuscleDistributionForm
        value={muscleDistribution}
        onChange={setMuscleDistribution}
      />

      {/* Fatica Sistemica */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Fatica Sistemica
        </label>
        <div className="flex gap-3">
          {Object.keys(FATICA_SISTEMICA).map((value) => (
            <label key={value} className="flex items-center gap-1.5 cursor-pointer">
              <input
                {...register("faticaSistemica")}
                type="radio"
                value={value}
                defaultChecked={exerciseToUpdate?.faticaSistemica === value}
                className="w-4 h-4 text-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {value.charAt(0) + value.slice(1).toLowerCase()}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Profilo di Resistenza */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Profilo di Resistenza
        </label>
        <div className="flex gap-3 flex-wrap">
          {Object.keys(PROFILO_RESISTENZA).map((value) => (
            <label key={value} className="flex items-center gap-1.5 cursor-pointer">
              <input
                {...register("profiloResistenza")}
                type="radio"
                value={value}
                defaultChecked={exerciseToUpdate?.profiloResistenza === value}
                className="w-4 h-4 text-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {value.charAt(0) + value.slice(1).toLowerCase()}
              </span>
            </label>
          ))}
        </div>
      </div>

      <label
        htmlFor="text"
        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
      >
        Tags
      </label>
      <div className="flex bg-gray-100 p-1 items-center w-full space-x-2 sm:space-x- rounded border border-gray-500 dark:bg-gray-700 dark:border-gray-300">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 opacity-50 dark:text-gray-100 ml-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>

        <input
          {...registerTag("tag", { required: "Inserisci tags validi" })}
          id="tag"
          className="bg-gray-100 outline-none text-sm sm:text-base w-full dark:bg-gray-700 dark:text-gray-200 border-transparent focus:border-transparent focus:ring-0"
          type="text"
          placeholder="Aggiungi un tag..."
        />
        <PlusButton variant={"icon"} onClick={handleSubmit(onTagSubmit)} />
      </div>
      {errors.tags && (
        <p
          role="alert"
          className="block mb-2 text-sm font-medium text-red-700 dark:text-red-500"
        >
          {errors.category?.message?.toString()}
        </p>
      )}
      <Tags
        tags={tags}
        removeTag={(tagToRm: string) =>
          setTags(tags.filter((tag: string) => tagToRm !== tag))
        }
      />
      {formBtn}
    </form>
  );
};

type TagsProps = {
  tags?: string[];
  removeTag: (tag: string) => void;
};
const Tags: React.FC<TagsProps> = ({ tags, removeTag }) => {
  return (
    <div className="my-3 flex flex-wrap -m-1">
      {tags?.map((tag) => (
        <span key={tag} className="m-1 flex flex-wrap justify-between items-center text-xs sm:text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded px-4 py-2 font-bold leading-loose cursor-pointer dark:text-gray-300">
          {tag}
          <button type="button" onClick={() => removeTag(tag)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-3 h-3 sm:h-4 sm:w-4 ml-4 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </span>
      ))}
    </div>
  );
};
